import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api/axios";
import { useCart } from "../context/CartContext";
import Footer from "../components/Footer";
import { trackViewItem, trackAddToCart } from "../utils/analytics";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const PALETTE = {
  Woody:  { bg: "#e8d9c4", accent: "#9b6e3a", light: "#f5ede0", emoji: "🪵" },
  Floral: { bg: "#f2e0e8", accent: "#c4607a", light: "#fdf0f5", emoji: "🌸" },
  Fresh:  { bg: "#d8eee6", accent: "#3a8a68", light: "#eef9f4", emoji: "🍃" },
  Spice:  { bg: "#f0e0cc", accent: "#c47a3a", light: "#fdf3e8", emoji: "🌶️" },
  default:{ bg: "#ede3d7", accent: "#e07b39", light: "#faf4ec", emoji: "🕯️" },
};

const CandlePlaceholder = ({ categoryName }) => {
  const palette = PALETTE[categoryName] || PALETTE.default;
  return (
    <div style={{
      width: "100%", height: "100%", background: `linear-gradient(145deg, ${palette.bg}, ${palette.light})`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px",
    }}>
      <div style={{ fontSize: "80px", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))" }}>
        {palette.emoji}
      </div>
      <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: palette.accent, opacity: 0.7 }}>
        {categoryName || "Candle"}
      </span>
    </div>
  );
};

export default function ProductDetails() {
  const { id }                    = useParams();
  const navigate                  = useNavigate();
  const { addToCart }             = useCart();

  const [product, setProduct]     = useState(null);
  const [related, setRelated]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);
  const [qty, setQty]             = useState(1);
  const [addedMsg, setAddedMsg]   = useState("");
  const [loginPrompt, setLoginPrompt] = useState(false);

  const isLoggedIn = !!localStorage.getItem("token");

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setQty(1);
    setAddedMsg("");
    window.scrollTo(0, 0);

    api.get(`products/${id}/`)
      .then(res => {
        setProduct(res.data);
        // Fetch related products from the same category
        return api.get("products/");
      })
      .then(res => {
        const all = res.data?.results || res.data || [];
        setRelated(
          all.filter(p => {
            if (p.id === parseInt(id)) return false;
            const pCat = typeof p.category === "object" ? p.category?.name : p.category;
            return pCat === (typeof res.data?.category === "object"
              ? res.data?.category?.name
              : res.data?.category);
          }).slice(0, 4)
        );
      })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Re-fetch related when product is set
  useEffect(() => {
    if (!product) return;
    trackViewItem(product);
    api.get("products/").then(res => {
      const all = res.data?.results || res.data || [];
      const catName = typeof product.category === "object" ? product.category?.name : product.category;
      setRelated(
        all.filter(p => {
          const pCat = typeof p.category === "object" ? p.category?.name : p.category;
          return p.id !== product.id && pCat === catName;
        }).slice(0, 4)
      );
    }).catch(() => {});
  }, [product]);

  const handleAddToCart = async () => {
    if (!isLoggedIn) {
      setLoginPrompt(true);
      setTimeout(() => setLoginPrompt(false), 3000);
      return;
    }
    try {
      await addToCart(product, qty); 
      trackAddToCart(product, qty);
      setAddedMsg(`${qty} item${qty > 1 ? "s" : ""} added to cart!`);
      setTimeout(() => setAddedMsg(""), 3000);
    } catch {
      setAddedMsg("Failed to add to cart.");
      setTimeout(() => setAddedMsg(""), 3000);
    }
  };

  const categoryName = typeof product?.category === "object"
    ? product?.category?.name
    : product?.category || "";
  const palette = PALETTE[categoryName] || PALETTE.default;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#faf7f2", paddingTop: "64px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px", animation: "pulse 1.5s infinite" }}>🕯️</div>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#aaa" }}>Loading product...</p>
      </div>
    </div>
  );

  // ── Not found ─────────────────────────────────────────────────────────────
  if (notFound) return (
    <div style={{ minHeight: "100vh", background: "#faf7f2", paddingTop: "64px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: "380px", padding: "32px" }}>
        <div style={{ fontSize: "64px", marginBottom: "16px" }}>🕯️</div>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#1a1a1a", marginBottom: "10px" }}>Product not found</h2>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#888", marginBottom: "24px" }}>
          This candle may have been removed or the link is incorrect.
        </p>
        <Link to="/products" style={{ display: "inline-block", padding: "12px 28px", borderRadius: "10px", background: "linear-gradient(135deg, #e07b39, #c96a18)", color: "#fff", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", textDecoration: "none" }}>
          Browse All Products
        </Link>
      </div>
    </div>
  );


  return (
    <div style={{ minHeight: "100vh", background: "#faf7f2", paddingTop: "64px" }}>

      {/* Login toast */}
      {loginPrompt && (
        <div style={{ position: "fixed", top: "80px", left: "50%", transform: "translateX(-50%)", background: "#1a1a1a", color: "#fff", padding: "12px 20px", borderRadius: "12px", fontFamily: "Georgia, serif", fontSize: "13px", zIndex: 9999, display: "flex", alignItems: "center", gap: "10px", boxShadow: "0 6px 24px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>
          🔒 Please log in to add items to cart.
          <button onClick={() => navigate("/login")} style={{ background: "#e07b39", color: "#fff", border: "none", borderRadius: "6px", padding: "4px 12px", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700" }}>Log In</button>
        </div>
      )}

      {/* Added to cart toast */}
      {addedMsg && (
        <div style={{ position: "fixed", top: "80px", left: "50%", transform: "translateX(-50%)", background: addedMsg.includes("Failed") ? "#c0392b" : "#1a1a1a", color: "#fff", padding: "12px 20px", borderRadius: "12px", fontFamily: "Georgia, serif", fontSize: "13px", zIndex: 9999, boxShadow: "0 6px 24px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>
          {addedMsg.includes("Failed") ? "❌" : "✅"} {addedMsg}
        </div>
      )}

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>

        {/* Breadcrumb */}
        <nav style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "28px" }}>
          <Link to="/" style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", textDecoration: "none" }}>Home</Link>
          <span style={{ color: "#ccc", fontSize: "12px" }}>›</span>
          <Link to="/products" style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", textDecoration: "none" }}>Products</Link>
          <span style={{ color: "#ccc", fontSize: "12px" }}>›</span>
          <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39" }}>{product.name}</span>
        </nav>

        {/* Main product grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "48px", marginBottom: "64px" }}>

          {/* Left — Image */}
          <div style={{ position: "relative" }}>
            <div style={{ borderRadius: "20px", overflow: "hidden", aspectRatio: "1", background: palette.bg, boxShadow: "0 12px 40px rgba(0,0,0,0.08)" }}>
              {product.image
                ? <img src={product.image} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:80px">${palette.emoji}</div>`; }} />
                : <CandlePlaceholder categoryName={categoryName} />
              }
            </div>

            {/* Category badge on image */}
            <div style={{ position: "absolute", top: "16px", left: "16px", padding: "5px 12px", borderRadius: "999px", background: "rgba(255,255,255,0.92)", backdropFilter: "blur(4px)", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: palette.accent }}>
              {palette.emoji} {categoryName}
            </div>


          </div>

          {/* Right — Info */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>



            <h1 style={{ fontFamily: "Georgia, serif", fontSize: "30px", fontWeight: "700", color: "#1a1a1a", lineHeight: "1.25", marginBottom: "10px" }}>
              {product.name}
            </h1>



            <div style={{ fontFamily: "Georgia, serif", fontSize: "32px", fontWeight: "700", color: "#e07b39", marginBottom: "20px" }}>
              {inr(product.price)}
            </div>

            {product.description && (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", color: "#666", lineHeight: "1.8", marginBottom: "28px", paddingBottom: "28px", borderBottom: "1px solid #f0ebe3" }}>
                {product.description}
              </p>
            )}

            {/* Quantity + Add to Cart */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>

                {/* Quantity selector */}
                <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e8d5b8", borderRadius: "10px", background: "#fffaf4" }}>
                  <button
                    type="button"
                    onClick={() => setQty(q => Math.max(1, q - 1))}
                    disabled={qty <= 1}
                    style={{ width: "40px", height: "46px", background: "none", border: "none",
                      cursor: qty <= 1 ? "not-allowed" : "pointer",
                      fontFamily: "Georgia, serif", fontSize: "20px",
                      color: qty <= 1 ? "#ddd" : "#888",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, lineHeight: 1 }}>
                    −
                  </button>
                  <span style={{ minWidth: "40px", textAlign: "center", fontFamily: "Georgia, serif",
                    fontSize: "16px", fontWeight: "700", color: "#1a1a1a", userSelect: "none" }}>
                    {qty}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQty(q => Math.min(99, q + 1))}
                    disabled={qty >= 99}
                    style={{ width: "40px", height: "46px", background: "none", border: "none",
                      cursor: qty >= 99 ? "not-allowed" : "pointer",
                      fontFamily: "Georgia, serif", fontSize: "20px",
                      color: qty >= 99 ? "#ddd" : "#888",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, lineHeight: 1 }}>
                    +
                  </button>
                </div>

                {/* Add to Cart */}
                <button
                  onClick={handleAddToCart}
                  style={{ flex: 1, padding: "13px 24px", borderRadius: "12px", background: "linear-gradient(135deg, #e07b39, #c96a18)", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", boxShadow: "0 6px 20px rgba(201,106,24,0.3)", transition: "transform 0.1s, box-shadow 0.1s" }}
                  onMouseDown={e => { e.currentTarget.style.transform = "scale(0.97)"; }}
                  onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                  🛒 Add to Cart
                </button>
              </div>

            {/* View cart shortcut */}
            <Link to="/cart" style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                View Cart →
              </Link>

            {/* Product meta */}
            <div style={{ marginTop: "28px", padding: "18px", background: "#fffaf4", borderRadius: "12px", border: "1px solid #f0ebe3" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                {[
                  { icon: "🏷️", label: "Category",   value: categoryName || "—" },

                  { icon: "🚚", label: "Shipping",    value: "Maharashtra only" },
                  { icon: "↩️", label: "Returns",     value: "7-day policy" },
                ].map(({ icon, label, value }) => (
                  <div key={label} style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <span style={{ fontSize: "14px", marginTop: "1px" }}>{icon}</span>
                    <div>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>{label}</p>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#1a1a1a", fontWeight: "600", margin: "2px 0 0" }}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Related Products ────────────────────────────────────────────── */}
        {related.length > 0 && (
          <section style={{ marginBottom: "48px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color: "#1a1a1a" }}>
                More in {categoryName}
              </h2>
              <Link to={`/products?category=${categoryName.toLowerCase()}`}
                style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39", textDecoration: "none" }}>
                View all →
              </Link>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "20px" }}>
              {related.map(p => {
                const pCatName = typeof p.category === "object" ? p.category?.name : p.category || "";
                const pPalette = PALETTE[pCatName] || PALETTE.default;
                return (
                  <Link key={p.id} to={`/products/${p.id}`} style={{ textDecoration: "none" }}>
                    <div style={{ background: "#fff", borderRadius: "16px", overflow: "hidden", border: "1px solid #f0ebe3", transition: "box-shadow 0.2s, transform 0.2s", cursor: "pointer" }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.10)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}>

                      <div style={{ aspectRatio: "1", background: pPalette.bg, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {p.image
                          ? <img src={p.image} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <span style={{ fontSize: "48px" }}>{pPalette.emoji}</span>}
                      </div>

                      <div style={{ padding: "14px" }}>
                        <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a", marginBottom: "4px" }}>{p.name}</p>
                        <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", color: "#e07b39" }}>{inr(p.price)}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}